
import {
  ref,
  computed,
  defineComponent,
  PropType,
  reactive,
  toRefs
} from 'vue'

import { useStore } from 'vuex'
import { format } from '@/utils/template'
import { Status } from '@/models/status'
import { STATUS, StatusDefinition } from '@/constants'
import { isUUID } from '@/utils'

export default defineComponent({
  name: 'OrderStatus',
  components: {},
  props: {
    id: {
      type: String,
      default: Date.now().toString(32)
    },
    modelValue: {
      type: Array as PropType<Array<Status>>,
      default: () => ([])
    },
    options: {
      type: Array as PropType<Array<Status | StatusDefinition>>,
      default: () => (null)
    },
    hide: {
      type: Array as PropType<Array<Status>>,
      default: () => ([])
    },
    optionsHeader: {
      type: String,
      default: null
    },
    request: {
      type: Function
    },
    labelVariant: {
      type: String,
      default: 'customLabel'
    },
    // TODO: refactor to name: "additive" to align with back-end StatusPattern
    combinable: {
      type: Boolean,
      default: true
    },
    shouldUpdate: {
      type: Boolean,
      default: false
    },
    updateField: {
      type: String,
      default: 'status'
    },
    loading: {
      type: Boolean,
      default: false
    },
    offset: {
      type: Array,
      default: () => ([-20, -30])
    },
    anchor: {
      type: String,
      default: 'top left'
    },
    setMetadata: {
      type: Boolean,
      default: false
    },
    metadataField: {
      type: String,
      default: 'status_info'
    },
    metadata: {
      type: Object,
      default: () => ({})
    }
  },
  emits: ['update:modelValue', 'select', 'toggle', 'remove'],
  setup (props, { emit }) {
    if (props.shouldUpdate && !isUUID(props.id)) {
      throw new Error('ItemStatus | "should-update" prop was set to true, but "id" prop was not a valid UUID')
    }

    // const $quasar = useQuasar()
    const $store = useStore()
    const $patch = props.request || $store.getters['Services/Directr'].patch
    const statusArray = computed<Array<Status>>({
      get () {
        return (props.modelValue || []).filter((status,i,a) => !props.hide.includes(status) && a.indexOf(status) === i)
      },
      set (value) {
        emit('update:modelValue', value)
      }
    })
    const showOptions = ref(false)

    const parsedOptions = computed(() => {
      return !props.options
        ? null
        : props.options.map(option => {
          return typeof option === 'string'
            ? STATUS.get(option)
            : Object.keys(option).includes(props.labelVariant) ? option : STATUS.get(option.id)
        }).concat((props.modelValue || []).map(s => STATUS.get(s)))
    })

    function toggle (show?:boolean) {
      showOptions.value = typeof show === 'boolean' ? show : !showOptions.value
      emit('toggle', showOptions.value)
    }

    function select (status: StatusDefinition, $event: Event) {
      $event.stopPropagation()
      let newStatus
      // NOTE: respects the "combinable" status prop. when a new status is set, only combinable statuses are kept by default
      if (props.combinable && !status.combinable) newStatus = statusArray.value.filter(s => STATUS.get(s).combinable).concat(status.id)
      else newStatus = statusArray.value.concat(status.id)
      statusArray.value = newStatus
      // emit('update:modelValue', statusArray.value)
      emit('select', status)
      toggle()

      if (props.shouldUpdate) {
        const updateData = { [props.updateField]: newStatus }
        // TODO: implement reason value input on setting additive status, for now defaults to "true"
        if (status.combinable && props.setMetadata) updateData[props.metadataField] = { [status.id]: { reason: { value: true } } }

        // perform the update using requestHandler
        $patch(props.id, updateData).then(updated => {
          // console.log('status updated', updated)
        }).catch(e => {
          console.error('ItemStatus | error during status patch request:', e)
        })
      }
    }

    function remove (status: StatusDefinition, optionIndex: number, $event: Event) {
      $event.stopPropagation()
      const newStatus = statusArray.value
      newStatus.splice(statusArray.value.indexOf(status.id), 1)
      statusArray.value = newStatus
      // emit('update:modelValue', statusArray.value)
      emit('remove', status, statusArray.value.indexOf(status.id))

      if (props.shouldUpdate) {
        const updateData = { [props.updateField]: newStatus } as any
        if (status.combinable && props.setMetadata) updateData[props.metadataField] = { [status.id]: { reason: { value: false } } }

        // perform the update using requestHandler
        $patch(props.id, updateData).then(updated => {
          // console.log('status removed', updated)
        }).catch(e => {
          console.error('ItemStatus | error during status patch request:', e)
        })
      }
    }

    function getMetadataTag (metadata: Record<string, any>, status) {
      return (typeof metadata?.[status]?.label === 'string' || typeof metadata?.[status]?.reason?.value === 'string')
        ? ` — ${metadata?.[status]?.label || metadata?.[status]?.reason?.value }`
        : ''
    }

    return {
      showOptions,
      parsedOptions,
      statusArray,
      getMetadataTag,
      Status,
      format,
      toggle,
      select,
      remove
    }
  }
})
